/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import {
	NAV_PAGE_LIST,
	ADMIN_ROLE_NUMBER,
	NAV_PAGE_LIST_CARGO,
	getAdminTableColumns,
	TABLE_COLUMNS,
} from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getRelationshipListSelector,
	getIsRelationshipListLoadingSelector,
	getPaginationSelector,
	getCompanyIdSelector,
} from './selectors';
import { getRelationshipListAction, resetStateAction } from './actions';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import {
	getIsAvailableRelationshipButtonsSelector,
	getIsAvailableRelationshipSettingsSectionSelector,
} from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../components/data-grid';

const RelationshipContainerWrapper = ({
	onGetRelationshipList,
	relationshipList,
	isRelationshipListLoading,
	pagination,
	onResetState,
	isAvailable,
	isAvailableSettings,
	companyId,
}) => {
	useInjectSaga({ key: 'relationshipContainerSaga', saga });
	useInjectReducer({ key: 'relationshipContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetRelationshipList({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'relationship');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetRelationshipList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetRelationshipList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetRelationshipList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetRelationshipList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={isAvailableSettings ? NAV_PAGE_LIST : NAV_PAGE_LIST_CARGO}
			pageContent={
				<DataGridComponent
					headerText="Мои ТЭО"
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					columns={companyId === ADMIN_ROLE_NUMBER ? getAdminTableColumns() : TABLE_COLUMNS()}
					rows={relationshipList}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isRelationshipListLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	relationshipList: getRelationshipListSelector(),
	isRelationshipListLoading: getIsRelationshipListLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableRelationshipButtonsSelector(),
	isAvailableSettings: getIsAvailableRelationshipSettingsSectionSelector(),
	companyId: getCompanyIdSelector(),
});

const mapDispatchToProps = {
	onGetRelationshipList: getRelationshipListAction,
	onResetState: resetStateAction,
};

const RelationshipContainer = connect(mapStateToProps, mapDispatchToProps)(RelationshipContainerWrapper);

RelationshipContainerWrapper.propTypes = {
	onGetRelationshipList: PropTypes.func.isRequired,
	relationshipList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			shipper: PropTypes.string,
			carrier: PropTypes.string,
			contract: PropTypes.string,
			conditions: PropTypes.string,
			link: PropTypes.string,
		}),
	),
	isRelationshipListLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isAvailableSettings: PropTypes.bool,
	companyId: PropTypes.number,
};

export default RelationshipContainer;
